import * as React from 'react';
import * as R from 'ramda';
import * as moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import { Form, Text, Select, FormValues, FormErrors, Checkbox, FormApi } from 'react-form';
import { BaseFormProps, BaseFormState } from '../../interfaces';
import { Tenant, Survey, OrganizationSurvey, SurveyReportType, OrganizationReportType, SurveyCompleteUser, UserTypeahead, User, PVAKeyValue, SurveyVersion, OrganizationText } from '../../models';
import { ValidationService, SurveyDataService, NotificationService, OrganizationDataService, TypeaheadService } from '../../services';
import { ImageUpload, SecondaryReportOrgUserForm, PonderaEditModal } from '../../components';
import Datepicker from 'react-datepicker';
import { InputGroup } from 'react-bootstrap';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { UserStore } from '../../stores';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';

interface OrganizationFormState extends BaseFormState {
    surveys: Survey[];
    selectedDate?: moment.Moment;
    datepickerTouched: boolean;
    confirmedAssociateTeamMembers: SurveyCompleteUser[];
    selectedAssociateTeamMembers?: UserTypeahead[];
    teamAssociateMemberTypeahead: {
        allowNew: boolean;
        isLoading: boolean;
        multiple: boolean;
        options: any[];
    };
    selectedOptKey: string;
    optValue: string;
    imageChanged: boolean;
    email: string;
    defaultImageDataUrl: string;
    imageDataLoaded: boolean;
}

interface FormProps extends BaseFormProps {
    tenants?: Tenant[];
    notificationService: NotificationService;
    UserStore?: UserStore;
}

@inject('UserStore') @observer
export class OrganizationForm extends React.Component<FormProps, OrganizationFormState> {
    private vs = new ValidationService();
    private typeaheadService = new TypeaheadService(this.props.notificationService);
    private surveyDataService = new SurveyDataService(this.props.notificationService);
    private organizationService = new OrganizationDataService(this.props.notificationService);
    private formApi: FormApi;
    private teamAssociateMemberTypeahead: any;

    constructor(props: FormProps) {
        super(props);

        let defaultDate = moment().add(6, 'months');

        if (this.props.defaultValues && this.props.defaultValues.expirationDate) {
            defaultDate = moment(this.props.defaultValues.expirationDate);
        }

        this.state = {
            canSubmit: false,
            formErrors: {},
            surveys: [],
            confirmedAssociateTeamMembers: [],
            datepickerTouched: false,
            selectedDate: defaultDate,
            teamAssociateMemberTypeahead: {
                allowNew: false,
                isLoading: false,
                multiple: true,
                options: [],
            },
            selectedOptKey: '',
            optValue: '',
            imageChanged: false,
            email: '',
            defaultImageDataUrl: '',
            imageDataLoaded: false,
        };

        this.props.defaultValues.organizationSurveys = this.props.defaultValues.organizationSurveys || [];
        this.props.defaultValues.organizationTexts = this.props.defaultValues.organizationTexts || [];

        this.validateErrors = this.validateErrors.bind(this);
        this.validateSuccess = this.validateSuccess.bind(this);

        this.handleAssociateSearch = this.handleAssociateSearch.bind(this);
        this.addAssociateUser = this.addAssociateUser.bind(this);
        this.removeAssociateUserBinder = this.removeAssociateUserBinder.bind(this);
        this.typeaheadAssociateOnChange = this.typeaheadAssociateOnChange.bind(this);
        
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    private handleAssociateSearch(filter: string) {
        const { teamAssociateMemberTypeahead, confirmedAssociateTeamMembers } = this.state;
        this.setState({ teamAssociateMemberTypeahead: Object.assign({}, teamAssociateMemberTypeahead, { isLoading: true }) });

        this.typeaheadService.getUserTypeaheadsByRole(this.props.defaultValues.id, filter)
            .then((response: UserTypeahead[]) => {
                const potentialMembers = response.filter((user) => !confirmedAssociateTeamMembers.some(x => x.userID === user.id));
                this.setState({
                    teamAssociateMemberTypeahead: Object.assign(
                        {},
                        teamAssociateMemberTypeahead,
                        {
                            isLoading: false,
                            options: potentialMembers
                        }
                    )
                });
            });
    }

    public componentDidMount() {
        if (this.props.defaultValues && this.props.defaultValues.tenantID) {
            this.surveyDataService.getAllByTenant(this.props.defaultValues.tenantID).then((surveys: Survey[]) => {
                this.organizationService.getLogoImageData(this.props.defaultValues.id).then((imageDataUrl: string) => {
                    this.setState({
                        surveys: surveys,
                        confirmedAssociateTeamMembers: this.props.defaultValues.surveyCompleteUsers,
                        defaultImageDataUrl: imageDataUrl,
                        imageDataLoaded: true,
                    });
                });
            });
        }
    }

    private typeaheadAssociateOnChange(selected: Array<Object | String>) {
        this.setState({ selectedAssociateTeamMembers: selected as UserTypeahead[] });
    }

    private addAssociateUser(e: any) {
        e.preventDefault();
        const { selectedAssociateTeamMembers, confirmedAssociateTeamMembers } = this.state;

        if (selectedAssociateTeamMembers && selectedAssociateTeamMembers.length > 0) {
            const newMembers = selectedAssociateTeamMembers.filter(user => {
                return !confirmedAssociateTeamMembers.some(x => x.userID === user.id);
            });

            const userTeams: SurveyCompleteUser[] = newMembers.map(user => {
                var u = new User();
                u.id = user.id;
                u.firstName = user.firstName;
                u.lastName = user.lastName;
                return {
                    id: 0,
                    userID: user.id,
                    user: u
                };
            });

            this.setState({
                confirmedAssociateTeamMembers: [...confirmedAssociateTeamMembers, ...userTeams],
                selectedAssociateTeamMembers: []
            });

            this.teamAssociateMemberTypeahead.getInstance().clear();
        }
    }

    private removeAssociateUser(userId: number) {
        const { confirmedAssociateTeamMembers } = this.state;

        this.setState({ confirmedAssociateTeamMembers: confirmedAssociateTeamMembers.filter((x: SurveyCompleteUser) => x.userID !== userId) });
    }

    private removeAssociateUserBinder(id: number): ((event: React.MouseEvent<HTMLElement>) => void) {

        return this.removeAssociateUser.bind(this, id);
    }

    // Validation methods
    validateErrors(values: FormValues) {
        const { name, tenantID } = values;
        let formErrors: any = {};

        if (name || name === '') {
            if (!this.vs.required(name)) {
                formErrors.name = 'Name is required.';
            } else if (!this.vs.cannotBeEmpty(name!)) {
                formErrors.name = 'Name cannot be blank.';
            }
        }

        if (tenantID) {
            if (!this.vs.required(tenantID)) {
                formErrors.tenantId = 'Tenant is required.';
            }
        }

        if (this.state.datepickerTouched && this.state.selectedDate == null) {
            formErrors.expirationDate = 'Expiration date is required';
        }

        return formErrors;
    }

    validateSuccess(values: FormValues, errors: FormErrors) {
        // make sure we have no errors and all the required values
        if (R.isEmpty(errors) && this.vs.validateRequiredFieldsExist(['name', 'tenantID'], values)) {
            this.setState({ canSubmit: true, formErrors: errors });
        } else {
            this.setState({ canSubmit: false, formErrors: errors });
        }

        return errors;
    }
    // end of validation stuff

    private addRemoveOrgSurvey(checked: boolean, survey: Survey, formValues: any) {
        if (checked) {
            var ind = formValues.organizationSurveys.findIndex((x: any) => {
                return x.surveyID === survey.id;
            });
            if (ind > -1) {
                formValues.organizationSurveys.splice(ind, 1);
            }
            this.forceUpdate();
        } else {
            var orgSurvey = new OrganizationSurvey();
            orgSurvey.surveyID = survey.id;
            orgSurvey.organizationID = formValues.id;
            formValues.organizationSurveys.push(orgSurvey);
            this.forceUpdate();
        }
    }

    private setDefault(surveyReport: SurveyReportType, formValues: any) {
        if (!formValues.organizationReportTypes) {
            formValues.organizationReportTypes = [];
        }

        var ind = formValues.organizationReportTypes.findIndex((x: any) => {
            return x.surveyReportTypeID === surveyReport.id;
        });
        if (ind > -1) {
            if (formValues.organizationReportTypes[ind].default) {
                formValues.organizationReportTypes[ind].default = false;
            } else {
                formValues.organizationReportTypes.map((ort: any) => {
                    ort.default = false;
                });
                formValues.organizationReportTypes[ind].default = true;
            }
            this.forceUpdate();
        }
    }

    private addRemoveOrgSurveyReport(checked: boolean, surveyReport: SurveyReportType, formValues: any) {
        if (!formValues.organizationReportTypes) {
            formValues.organizationReportTypes = [];
        }
        
        if (checked) {
            var ind = formValues.organizationReportTypes.findIndex((x: any) => {
                return x.surveyReportTypeID === surveyReport.id;
            });
            if (ind > -1) {
                formValues.organizationReportTypes.splice(ind, 1);
            }
            this.forceUpdate();
        } else {
            var orgSurvey = new OrganizationReportType();
            orgSurvey.surveyReportTypeID = surveyReport.id;
            orgSurvey.organizationID = formValues.id;
            formValues.organizationReportTypes.push(orgSurvey);
            this.forceUpdate();
        }
    }
    
    private tenantChange(e: any, formValues: any) {
        var tenantId: number = Number(e);
        formValues.organizationSurveys = [];
        this.forceUpdate();
        this.surveyDataService.getAllByTenant(tenantId).then((surveys: Survey[]) => {
            this.setState({
                surveys: surveys
            });
        });
    }

    handleImageUpload = (file: File) => {
        if (this.props.defaultValues) {
            this.organizationService.uploadImage(this.props.defaultValues.id, file);
            this.setState({imageChanged: false});
        }
    }

    handleDatepickerOnChange = (newDate: moment.Moment) => {
        this.setState({ selectedDate: newDate }, () => { this.formApi.setTouched('expirationDate', true); });
    }

    markDatePickerTouched = () => {
        this.setState({ datepickerTouched: true });
    }

    datePickerBlur = () => {
        this.formApi.setTouched('expirationDate', true);
    }

    setFormApi = (api: FormApi) => {
        this.formApi = api;
    }

    handleSubmit = (values: any) => {
        let canContinue = true;
        if (this.state.imageChanged) {
            canContinue = confirm('You\'ve changed the organization logo, but haven\'t uploaded it. Are you sure you want to submit without uploading?');
        }

        if (canContinue) {
            if (this.state.selectedDate) {
                values.expirationDate = this.state.selectedDate.toDate();
            }
            values.surveyCompleteUsers = this.state.confirmedAssociateTeamMembers;

            this.props.submit(values);
        }
    }

    handleImageChanged = () => {
        this.setState({imageChanged: true});
    }

    private sendTestEmail(orgId: number, surveyId: number) {
        if (this.props.defaultValues) {
            this.organizationService.testEmail(orgId, surveyId);
        }
    }

    public render() {
        const { canSubmit, formErrors, teamAssociateMemberTypeahead, confirmedAssociateTeamMembers } = this.state;
        const tenantOptions = !this.props.tenants ? [] : this.props.tenants.map((tenant) => {
            return {
                label: tenant.name,
                value: tenant.id
            };
        });

        let imageUploadComponent: any = null;
        if (this.state.imageDataLoaded) {
            imageUploadComponent = (<ImageUpload maxWidth={500} maxHeight={300} imageChangedHandler={this.handleImageChanged} uploadHandler={this.handleImageUpload} imageBase64String={this.state.defaultImageDataUrl} />);
        }

        var logoStyleOptions: any[] = [{
            label: 'Solid',
            value: 'solid'
        }, {
            label: 'Transparent White',
            value: 'transparentOnWhite'
        }, {
            label: 'Transparent No Background',
            value: 'transparentNoBackground'
        }];

        var logoSizeStyleOptions: any[] = [{
            label: 'Square',
            value: 'square'
        }, {
            label: 'Banner',
            value: 'banner'
        }, {
            label: 'Vertical',
            value: 'vertical'
        }];

        const languageOpts: any = [{
            label: 'English',
            value: 1
        }];

        var surveyOpts: any[] = [];
        this.state.surveys.map((survey: Survey, index: number) => {
            surveyOpts.push({
                label: survey.name,
                value: survey.id
            });
        });

        return (
            <div>
                <Form
                    defaultValues={this.props.defaultValues}
                    onSubmit={submittedValues => this.handleSubmit(submittedValues)}
                    validateError={this.validateErrors}
                    validateSuccess={this.validateSuccess}
                    getApi={this.setFormApi}
                >
                    {formApi => (
                        <form onSubmit={formApi.submitForm} id="entityForm">
                            <div className="form-group">
                                <label htmlFor="name">Name *</label>
                                <Text className="form-control" field="name" id="name" />
                                {formErrors.name && <span className="help-block error-text">{formErrors.name}</span>}
                            </div>
                            <div className="form-group">
                                <label htmlFor="tenantID">Tenant *</label>
                                <Select className="form-control" field="tenantID" id="tenantID" options={tenantOptions} onChange={(e) => this.tenantChange(e, formApi.values)} />
                                {formErrors.name && <span className="help-block error-text">{formErrors.tenantID}</span>}
                            </div>
                            <div className="form-group">
                                <label htmlFor="expirationDate" className="control-label">Expiration Date *</label>
                                <Datepicker
                                    selected={this.state.selectedDate}
                                    showMonthDropdown
                                    showYearDropdown
                                    className="form-control"
                                    onChange={this.handleDatepickerOnChange}
                                    dropdownMode="select"
                                    minDate={moment()}
                                    onFocus={this.markDatePickerTouched}
                                    onBlur={this.datePickerBlur}
                                />

                                {this.state.formErrors.expirationDate && <span className="help-block error-text">{this.state.formErrors.expirationDate}</span>}
                            </div>
                            <div className="form-group">
                                <label htmlFor="isLicensee">Is Licensee?</label>
                                <Checkbox className="checkbox" field="isLicensee" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="hasSubscription">Has Subscription?</label>
                                <Checkbox className="checkbox" field="hasSubscription" />
                            </div>

                            <hr />
                            <h3>Surveys</h3>
                            <table className="table table-striped">
                                {this.state.surveys.map((survey: Survey, index: number) => {
                                    var checked: boolean = false;
                                    var selectedSurveyVersionId = 0;
                                    if (formApi.values.organizationSurveys) {
                                        var s = formApi.values.organizationSurveys.find((x: any) => {
                                            return x.surveyID === survey.id;
                                        });
                                        if (s !== undefined) {
                                            checked = true;
                                            selectedSurveyVersionId = s.surveyVersionID;
                                        }
                                    }
                                    return <tbody key={`survey-${index}`}>
                                        <tr>
                                            <td style={{ width: '50px' }}>
                                                <input className="form-check-input" type="checkbox" checked={checked} onChange={(e) => this.addRemoveOrgSurvey(checked, survey, formApi.values)} />
                                            </td>
                                            <td>
                                                <span>{survey.name}</span>
                                            </td>
                                            <td>
                                                {checked && <select className="form-control" value={selectedSurveyVersionId} onChange={(e: any) => this.setDefaultSV(formApi, survey, e)}>
                                                    <option value={0}>- Default Survey Version -</option>
                                                    {survey.surveyVersions.map((sv: SurveyVersion) => {
                                                        return <option key={`sv-${sv.id}`} value={sv.id}>{sv.nickname}</option>;
                                                    })}
                                                </select>}
                                            </td>
                                        </tr>
                                        {checked && <tr>
                                            <td colSpan={3}>
                                                <div style={{paddingLeft: '25px'}}>
                                                    <table className="table table-striped">
                                                        <thead>
                                                            <tr>
                                                                <td>
                                                                    Active
                                                                </td>
                                                                <td>
                                                                    Default
                                                                </td>
                                                                <td>
                                                                    Survey Report Type
                                                                </td>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {survey.surveyReportTypes.map((report: SurveyReportType, reportInd: number) => {
                                                                var checkedReport: boolean = false;
                                                                var defaultReport: boolean = false;
                                                                if (formApi.values.organizationReportTypes) {
                                                                    var r = formApi.values.organizationReportTypes.find((x: OrganizationReportType) => {
                                                                        return x.surveyReportTypeID === report.id;
                                                                    });
                                                                    if (r !== undefined) {
                                                                        checkedReport = true;
                                                                        defaultReport = r.default;
                                                                    }
                                                                }
                                                                return <tr key={`survey-${index}-report-${reportInd}`}>
                                                                    <td>
                                                                        <input className="form-check-input" type="checkbox" checked={checkedReport} onChange={(e) => this.addRemoveOrgSurveyReport(checkedReport, report, formApi.values)} />
                                                                    </td>
                                                                    <td>
                                                                        <input className="form-check-input" type="checkbox" checked={defaultReport} onChange={(e) => this.setDefault(report, formApi.values)} />
                                                                    </td>
                                                                    <td>
                                                                        {report.name}
                                                                    </td>
                                                                </tr>;
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                            </tr>
                                        }
                                    </tbody>;
                                })}
                            </table>
                            <hr />

                            {imageUploadComponent &&
                                <div>
                                    <h3>Logo</h3>
                                    {imageUploadComponent}
                                    < br />

                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="logoStyle">Logo Style</label>
                                                <Select className="form-control" field="logoStyle" id="logoStyle" options={logoStyleOptions} />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="logoSizeStyle">Size Style</label>
                                                <Select className="form-control" field="logoSizeStyle" id="logoSizeStyle" options={logoSizeStyleOptions} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="showLogoInNav">Show Logo In Nav?</label>
                                                <Checkbox className="checkbox" field="showLogoInNav" />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="useWhiteBackgroundInNav">Use White Background In Nav?</label>
                                                <Checkbox className="checkbox" field="useWhiteBackgroundInNav" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }

                            <div>
                                <h3>Survey Complete Members</h3>
                                <div><i>Receives an e-mail when a user from this organization completes an survey.</i></div>
                                <div className="form-group">
                                    <InputGroup>
                                        <AsyncTypeahead
                                            ref={(typeahead: any) => this.teamAssociateMemberTypeahead = typeahead}
                                            {...teamAssociateMemberTypeahead}
                                            minLength={2}
                                            onSearch={this.handleAssociateSearch}
                                            onChange={this.typeaheadAssociateOnChange}
                                            placeholder="Search for a user"
                                            labelKey={(option) => `${option.lastName}, ${option.firstName}`}
                                        />
                                        <InputGroup.Button>
                                            <button onClick={this.addAssociateUser} className="btn btn-default">Add</button>
                                        </InputGroup.Button>
                                    </InputGroup>
                                </div>
                                {(confirmedAssociateTeamMembers && confirmedAssociateTeamMembers.length > 0) &&
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {confirmedAssociateTeamMembers.map((userteam, index) => (
                                                <tr key={`user-${userteam.userID}`}>
                                                    <td>
                                                        {userteam.user!.lastName}, {userteam.user!.firstName}
                                                    </td>
                                                    <td>
                                                        <span onClick={this.removeAssociateUserBinder(userteam.user!.id)} className="glyphicon glyphicon-trash" />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                }
                            </div>

                            <h3>Report Name Overrides</h3>

                            <table className="table table-striped">
                                <tbody>
                                    { formApi.values.pvaKeyValues &&
                                        formApi.values.pvaKeyValues.map((keyValue: PVAKeyValue, index: number) => {
                                            return <tr key={`key-value-${index}`}>
                                                <td>
                                                    {keyValue.key}
                                                </td>
                                                <td>
                                                    <Text className="form-control" field={['pvaKeyValues', index, 'value']} />
                                                </td>
                                                <td>
                                                    <a className="btn btn-danger" onClick={(e) => this.removeKeyValue(formApi, index)}>Remove</a>
                                                </td>
                                            </tr>;
                                        })
                                    }
                                </tbody>
                            </table>

                            <table className="table table-striped">
                                <tbody>
                                    <tr>
                                        <td>
                                            {this.availableKeysSelect(formApi)}
                                        </td>
                                        <td>
                                            <input disabled={this.state.selectedOptKey === ''} type="text" className="form-control" value={this.state.optValue} onChange={(e) => this.setState({ optValue: e.target.value })} />
                                        </td>
                                        <td>
                                            <a className="btn btn-primary" onClick={(e) => this.addKeyValue(formApi)}>Add</a>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <hr />
                            <SecondaryReportOrgUserForm notificationService={this.props.notificationService} orgId={this.props.defaultValues.id} />

                            <hr />
                            {['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) && <div>
                                <h3>Email</h3>
                                {this.textBlock(formApi, surveyOpts, languageOpts, 'email')}
                            </div>}

                            <hr />

                            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>Save</button>
                        </form>
                    )}
                </Form>
            </div>
        );
    }

    private textBlock(formApi: any, surveyOptions: any[], languageOpts: any[], type: string) {

        var availableKeys = (
            <table className="table table-striped">
                <thead>
                    <tr>
                        <td>Key</td>
                        <td>Description</td>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>#{'{'}FIRSTNAME{'}'}</td>
                        <td>The first name of the user.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}LASTNAME{'}'}</td>
                        <td>The last name of the user.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}ORGANIZATION{'}'}</td>
                        <td>The name of the organization the user is in.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}TOTALSTATEMENTS{'}'}</td>
                        <td>The total number of statements in the survey.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}TRUEFALSECOUNT{'}'}</td>
                        <td>The total number of true/false statements in the survey.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}LIKERTCOUNT{'}'}</td>
                        <td>The total number of likert statements in the survey.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}SURVEYEXPIRATION{'}'}</td>
                        <td>The date the survey will expire.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}ASSESSMENTLINKSHORT{'}'}</td>
                        <td>Clickable short link to the survey, labeled 'Take the Assessment'.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}ASSESSMENTLINK{'}'}</td>
                        <td>Clickable long link to the survey, is the full http:// address.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}SENDERFIRSTNAME{'}'}</td>
                        <td>The first name of the person sending the survey invite.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}SENDERLASTNAME{'}'}</td>
                        <td>The last name of the person sending the survey invite.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}SENDEREMAIL{'}'}</td>
                        <td>The email address of the person sending the survey invite.</td>
                    </tr>
                    <tr>
                        <td>#{'{'}SENDERORGANIZATION{'}'}</td>
                        <td>The name of the organization of the person sending the survey invite.</td>
                    </tr>
                </tbody>
        </table>);
        
        return (
            <div>
                <table className="table">
                    <tr>
                        <td>
                            Survey
                        </td>
                        <td>
                            Language
                        </td>
                        <td>
                            Text
                        </td>
                    </tr>
                    {
                        formApi.values.organizationTexts.map((text: OrganizationText, index: number) => {
                            if (text.textType !== type) {
                                return;
                            }

                            const props = {
                                show: text.showModal === true,
                                onHide: (data: string) => {
                                    text.showModal = false;
                                    text.value = data;
                                    this.forceUpdate();
                                },
                                title: 'Content',
                                content: text.value || '',
                                extra: availableKeys
                            };

                            return <tr key={`styletext-${index}`}>                                
                                <td>
                                    <Select className="form-control" field={['organizationTexts', index, 'surveyID']} options={surveyOptions} />
                                </td>
                                <td>
                                    <Select className="form-control" field={['organizationTexts', index, 'languageId']} options={languageOpts} />
                                </td>
                                <td>
                                    <a className="btn btn-default" style={{marginRight: '10px;'}} onClick={(e) => { text.showModal = true; this.forceUpdate(); }}>View/Edit</a>
                                    <a className="btn btn-danger" style={{marginRight: '10px;'}} onClick={(e) => { this.delText(formApi, index); this.forceUpdate(); }}>Delete</a>
                                    <a className="btn btn-warning" onClick={(e) => this.sendTestEmail(formApi.values.id, formApi.values.organizationTexts[index].surveyID)}>Send Test Email</a>
                                    <PonderaEditModal {...props} />
                                </td>
                            </tr>;
                        })
                    }
                </table>
                <div className="form-group">
                    <div className="row">
                        <div className="col-xs-4">
                            <a className="btn btn-default form-control" onClick={(e) => this.add(formApi.values, type)}>Add</a>
                        </div>
                    </div>
                </div>
            </div>);
    }

    private delText(formApi: any, index: number) {
        if (confirm('Are you sure you want to delete this text?')) {
            var orgTexts = formApi.values.organizationTexts;
            orgTexts.splice(index, 1);
            formApi.setValue('organizationTexts', orgTexts);
        }
    }

    private add(formValues: any, type: string) {
        var st = new OrganizationText();
        st.textType = type;
        st.languageId = 1;
        st.keyName = type;
        formValues.organizationTexts.push(st);
        this.forceUpdate();
    }

    private setDefaultSV(formApi: any, survey: Survey, e: any) {
        var svId = parseInt(e.target.value, 10);
        
        var orgSurveys = formApi.values.organizationSurveys;
        if (orgSurveys) {
            var sInd = orgSurveys.findIndex((x: any) => {
                return x.surveyID === survey.id;
            });
            if (sInd > -1) {
                if (svId === 0) {
                    orgSurveys[sInd].surveyVersionID = undefined;
                } else {
                    orgSurveys[sInd].surveyVersionID = svId;
                }
                formApi.setValue('organizationSurveys', orgSurveys);
                this.forceUpdate();
            }
        }
    }

    private removeKeyValue(formApi: any, index: number) {
        var keys = formApi.values.pvaKeyValues;
        keys.splice(index, 1);
        formApi.setValue('pvaKeyValues', keys);
    }

    private addKeyValue(formApi: any) {
        var keys = formApi.values.pvaKeyValues;
        var keyValue = new PVAKeyValue();
        keyValue.key = this.state.selectedOptKey;
        keyValue.value = this.state.optValue;
        keys.push(keyValue);
        formApi.setValue('pvaKeyValues', keys);
        this.setState({
            selectedOptKey: '',
            optValue: ''
        });
    }

    private availableKeysSelect(formApi: any) {
        var opts = this.availableKeyOverrides(formApi);
        return (
            <select className="form-control" value={this.state.selectedOptKey} onChange={(e) => this.setState({ selectedOptKey: e.target.value })}>
                <option value={''}>- Select -</option>;
                {opts.map((opt: string, pairInd: number) => {
                    return <option key={`pair-${pairInd}`} value={opt}>{opt}</option>;
                })}
            </select>
        );
    }

    private availableKeyOverrides(formApi: any) {
        var list = ['myReport', 'individualReport', 'comparisonReport', 'myInteractionReport', 'interactionReport', '9Box', 'teamReport', 'teamologyReport', 'teamListViewReport', 'teamListViewReportRI', 'teamGridReport', 'scoreCard'];
        
        if (formApi.values.pvaKeyValues) {
            formApi.values.pvaKeyValues.map((keyValue: PVAKeyValue) => {
                var ind = list.indexOf(keyValue.key);
                if (ind !== -1) {
                    list.splice(ind, 1);
                }
            });
        }
        return list;
    }
}


// WEBPACK FOOTER //
// ./src/components/organization/OrganizationForm.tsx